import { useState, useCallback } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import axios from 'axios';
import type { ExternalValidation, FormData, SubmitArgs } from '@milanlaser/mlcomponents';
import {
    callVerifyHuman,
    validateWithPossibleNow,
    NETLIFY_FUNCTIONS_PATH,
    syncPardotTracking,
    isLeadsCaptureResponse,
    getFacebookCookies,
    getOptimizelyData,
    appendFormUrlToExposures,
    safeFullStory as FullStory,
} from '../utils';
import { useSelfScheduleTimer, SELF_SCHEDULE_EVENTS } from './useSelfScheduleTimer';

interface FormSubmissionConfig {
    couponCode?: string;
    recaptchaAction?: string;
}

interface UseFormSubmissionReturn {
    externalValidation: ExternalValidation;
    handleValidationRequest: (formData: FormData) => Promise<boolean>;
    handleFormSubmission: (formData: FormData, args: SubmitArgs) => Promise<{ success: boolean }>;
    setExternalValidation: React.Dispatch<React.SetStateAction<ExternalValidation>>;
    isFormSubmissionSuccess: boolean;
    formType: 'full' | 'partial' | undefined;
}

const trackingEventNames = {
    fullFormSuccess: 'Influencer Consult Form - Success',
    fullFormError: 'Influencer Consult Form - Error',
    partialFormSuccess: 'Influencer Partial Form - Success',
    partialFormError: 'Influencer Partial Form - Error',
    fullFormPardotSkipped: 'Influencer Consult Form - Pardot Sync Skipped',
    partialFormPardotSkipped: 'Influencer Partial Form - Pardot Sync Skipped',
};

const getFormType = (formType: string | undefined) => {
    if (!formType) return {};
    switch (formType) {
        case 'partial':
            return { formType: 'brandPartner' };
        default:
            // formSource distinguishes this from the other three 'Self Schedule' forms.
            return { consultType: 'Self Schedule', formSource: 'brandPartner' };
    }
};

const getFormSubmissionEndpoint = (formType: string | undefined) => {
    if (formType && formType === 'partial') {
        return `${NETLIFY_FUNCTIONS_PATH}form-handler`;
    }
    return `${NETLIFY_FUNCTIONS_PATH}consult-form-handler`;
};

const getRecaptchaActionName = (formData: FormData | undefined) => {
    // partial form submission doesn't use first name or last name
    const formType = formData?.first_name ? 'full' : 'partial';
    if (formType && formType === 'partial') {
        return 'BrandPartnerPartialFormSubmit';
    }
    return 'BrandPartnerFullFormSubmit';
};

export function useBrandPartnerFormSubmission(config: FormSubmissionConfig = {}): UseFormSubmissionReturn {
    const { couponCode } = config;

    const { executeRecaptcha } = useGoogleReCaptcha();
    const isBrowser = typeof window !== 'undefined';

    const [externalValidation, setExternalValidation] = useState<ExternalValidation>({
        isValidating: false,
        validationError: undefined,
        isValid: false,
    });
    const [isFormSubmissionSuccess, setIsFormSubmissionSuccess] = useState<boolean>(false);
    const [formType, setFormType] = useState<UseFormSubmissionReturn['formType']>(undefined);
    const { startTimer, pushTimingEvent, resetTimer } = useSelfScheduleTimer();

    const handleReCaptchaVerify = useCallback(
        async (formData?: FormData) => {
            if (!executeRecaptcha) {
                console.log('Execute recaptcha not yet available');
                return;
            }
            const user = await callVerifyHuman(getRecaptchaActionName(formData), executeRecaptcha);
            console.log('ReCaptcha verification result:', user);
            return user;
        },
        [executeRecaptcha]
    );

    const handleValidationRequest = useCallback(
        async (formData: FormData) => {
            try {
                setExternalValidation({ isValidating: true, validationError: undefined, isValid: false });

                const user = await handleReCaptchaVerify(formData);
                if (user === 'bot') {
                    setExternalValidation({ isValidating: false, validationError: 'bot detected', isValid: false });
                    return false;
                } else {
                    setExternalValidation({ isValidating: false, validationError: undefined, isValid: true });
                    return true;
                }
            } catch (error) {
                if (error instanceof Error) {
                    console.error('Validation request error:', error.message);
                    setExternalValidation({
                        isValidating: false,
                        validationError: error instanceof Error ? error.message : 'Unknown error',
                        isValid: false,
                    });
                    return false;
                }
            }
            return false;
        },
        [handleReCaptchaVerify]
    );

    const handleFormSubmission = useCallback(
        async (formData: FormData, args: SubmitArgs) => {
            const { fbp, fbc } = getFacebookCookies();
            const { experiment_exposures, experiment_user_ID } = getOptimizelyData();

            const isFullForm = args?.formType === 'full' && args?.isValid;
            const successEventName = isFullForm
                ? trackingEventNames.fullFormSuccess
                : trackingEventNames.partialFormSuccess;
            const errorEventName = isFullForm ? trackingEventNames.fullFormError : trackingEventNames.partialFormError;

            // Start timer for full form (self-schedule) flow
            if (isFullForm) {
                startTimer({
                    form_type: 'brand_partner',
                });
            }

            try {
                // Validate phone and email
                const { phoneFound, wireless, emailFound, result, flags } = await validateWithPossibleNow(
                    formData.phone,
                    formData.email
                );

                // Build base submission data
                const submissionData: any = {
                    ...formData,
                    ...getFormType(args?.formType),
                    marketing_sms_optout: false,
                    updates: true,
                    cloudamp__data__c: localStorage.getItem('cloudamp__data__c') || '',
                    phoneFound,
                    wireless,
                    emailFound,
                    result,
                    flags,
                    fbp,
                    fbc,
                    experiment_exposures: appendFormUrlToExposures(experiment_exposures),
                    experiment_user_ID,
                };

                submissionData.city = formData?.metaData?.clinicData?.metroName;
                submissionData.state = formData?.metaData?.clinicData?.stateAbbrev;
                submissionData.coupon_code = couponCode || '';
                // `last_name` is required by Pardot/Salesforce. For influencer partial forms,
                // where only email and phone are collected, we fall back to using the email as
                // `last_name` to prevent submission errors and empty name fields in SF.
                submissionData.last_name = formData.last_name || formData.email;

                setExternalValidation({ isValidating: false, validationError: undefined, isValid: true });
                setFormType(args?.formType);

                // Submit form
                const response = await axios.post(getFormSubmissionEndpoint(args?.formType), submissionData);

                // Only sync Pardot tracking on the legacy path; leads-capture already wrote the lead.
                if (
                    response?.status === 200 &&
                    response?.data?.message === 'Form submitted successfully' &&
                    !isLeadsCaptureResponse(response)
                ) {
                    syncPardotTracking(formData);
                }

                // Validate response based on form type
                if (isFullForm && (!isBrowser || !response?.data?.redirectUrl)) {
                    throw new Error('No redirect URL returned from backend');
                }
                if (!isFullForm && !response?.data) {
                    throw new Error('No response data returned from backend');
                }

                // Track success
                if (successEventName) {
                    FullStory('trackEvent', {
                        name: successEventName,
                        properties: {
                            user_email: formData.email,
                        },
                    });
                }

                // Handle redirect for full form
                if (isFullForm && isBrowser) {
                    // Push timing event to GTM before redirect
                    // Wait for event to be sent before redirecting
                    await pushTimingEvent(SELF_SCHEDULE_EVENTS.TIMING, {
                        form_type: 'brand_partner',
                    });

                    window.location.href = response.data.redirectUrl;
                }
                setIsFormSubmissionSuccess(true);
                return { success: true };
            } catch (error) {
                console.error('Form submission error:', error);
                const errorMessage = error instanceof Error ? error.message : 'Form submission error';

                // Track failure and reset timer on error for full form
                if (isFullForm) {
                    await pushTimingEvent(SELF_SCHEDULE_EVENTS.FAILURE, {
                        form_type: 'brand_partner',
                        error_type: errorMessage,
                    });
                    await resetTimer();
                }

                setExternalValidation({
                    isValidating: false,
                    validationError: errorMessage,
                    isValid: false,
                });

                // Track error with enhanced details
                if (errorEventName) {
                    FullStory('trackEvent', {
                        name: errorEventName,
                        properties: {
                            name: errorEventName,
                            user_email: formData.email,
                            error_message: errorMessage,
                            error_details: axios.isAxiosError(error)
                                ? {
                                      status: error.response?.status,
                                      statusText: error.response?.statusText,
                                  }
                                : 'Non-axios error',
                        },
                    });

                    // Log that Pardot tracking was skipped due to form submission failure
                    FullStory('trackEvent', {
                        name: isFullForm
                            ? trackingEventNames.fullFormPardotSkipped
                            : trackingEventNames.partialFormPardotSkipped,
                        properties: {
                            user_email: formData.email,
                            error_message: errorMessage,
                            reason: 'Actual Form submission failed',
                        },
                    });
                }
                return { success: false };
            }
        },
        [couponCode, isBrowser, startTimer, pushTimingEvent, resetTimer]
    );

    return {
        externalValidation,
        handleValidationRequest,
        handleFormSubmission,
        setExternalValidation,
        isFormSubmissionSuccess,
        formType,
    };
}
